// src/TruckList.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import TruckForm from './TruckForm.jsx';
import { api } from './lib/apiFetch';

// API path: trucks are type=200
const TRUCKS_PATH = '/alltrucks?type=200';

// Normalize ANY backend shape to a list we can render safely
const toList = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.items)) return data.items;
  if (data && Array.isArray(data.data))  return data.data;
  return [];
};

const displayAssignment = (t = '') => t || '—';

export default function TruckList() {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [sort, setSort] = useState({ key: 'unitNumber', dir: 'asc' });
  const [search, setSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const fetchTrucks = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const resp = await api.get(TRUCKS_PATH); // api helper adds base URL + Authorization
      const list = toList(resp);

      if (!Array.isArray(list)) {
        console.error('TruckList: unexpected response shape', resp);
        setErr('Unexpected response from server.');
        setRows([]);
        return;
        }
      setRows(list);
    } catch (e) {
      console.error('TruckList fetch error:', e);
      setErr(String(e?.message || e));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void fetchTrucks(); }, [fetchTrucks]);

  const requestSort = (key) => {
    setSort(prev => ({
      key,
      dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handleRowClick = (truck) => setSelected({ ...truck });

  const handleAdd = () => {
    // New truck; /updatetruck will create when id is omitted
    setSelected({
      unitNumber: '',
      manufacturer: '',
      style: '',
      assignment: '',
      unitStatus: 'A',
    });
  };

  const filteredSorted = useMemo(() => {
    let list = Array.isArray(rows) ? [...rows] : [];

    if (!includeInactive) list = list.filter(t => (t.unitStatus || 'A') !== 'I');

    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(t =>
        [t.unitNumber, t.manufacturer, t.style, t.assignment, t.unitStatus]
          .filter(Boolean)
          .some(v => String(v).toLowerCase().includes(q))
      );
    }

    const { key, dir } = sort;
    const mul = dir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      const av = (a?.[key] ?? '').toString().toLowerCase();
      const bv = (b?.[key] ?? '').toString().toLowerCase();
      if (av < bv) return -1 * mul;
      if (av > bv) return  1 * mul;
      return 0;
    });

    return list;
  }, [rows, includeInactive, search, sort]);

  return (
    <div className="container listDiv">
      <div className="search-and-add-bar" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Search trucks…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
          />
          Include inactive
        </label>
        <button onClick={handleAdd}>Add Truck</button>
        <button onClick={fetchTrucks} disabled={loading}>{loading ? 'Loading…' : 'Refresh'}</button>
      </div>

      {err && <div style={{ color: '#C80036', margin: '8px 0' }}>{err}</div>}

      <div className="table-container">
        {loading ? (
          <div>Loading…</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th className="styleCol1" onClick={() => requestSort('unitNumber')}>Unit</th>
                <th className="styleCol2" onClick={() => requestSort('manufacturer')}>Manufacturer</th>
                <th className="styleCol1" onClick={() => requestSort('style')}>Style</th>
                <th className="styleCol2" onClick={() => requestSort('assignment')}>Assignment</th>
                <th className="styleCol1" onClick={() => requestSort('unitStatus')}>Status</th>
              </tr>
            </thead>
            <tbody className="scrollable-tbody">
              {filteredSorted.map((t, idx) => (
                <tr key={t.id || t.unitNumber || `row-${idx}`} onClick={() => handleRowClick(t)}>
                  <td className="styleCol1">{t.unitNumber || '—'}</td>
                  <td className="styleCol2">{t.manufacturer || '—'}</td>
                  <td className="styleCol1">{t.style || '—'}</td>
                  <td className="styleCol2">{displayAssignment(t.assignment)}</td>
                  <td className="styleCol1">{t.unitStatus === 'I' ? 'Inactive' : 'Active'}</td>
                </tr>
              ))}
              {!loading && !err && filteredSorted.length === 0 && (
                <tr><td colSpan={5} style={{ color: '#666' }}>No trucks found. (GET {TRUCKS_PATH})</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {selected && (
        <div className="container formDiv">
          <TruckForm
            truck={selected}
            onCancelEdit={() => setSelected(null)}
            onSuccessfulSubmit={fetchTrucks}
          />
        </div>
      )}
    </div>
  );
}
